import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { EASE, viewportOnce, stagger, fadeUp } from "@/lib/motion";
import { IMAGES } from "@/data/content";
import { Seo } from "@/components/Seo";

const OFFERINGS = [
  {
    step: "Music Therapy",
    body: "The heart of our practice — using rhythm, melody and evidence-based methods to support emotional, cognitive and physical wellbeing.",
  },
  {
    step: "Sound-based Wellness",
    body: "Immersive sound experiences designed to relax the nervous system, soften the mind and restore a natural sense of balance.",
  },
  {
    step: "Reiki Energy Healing",
    body: "Gentle energy work that clears, calms and re-attunes — creating space for the body’s own healing rhythm to return.",
  },
  {
    step: "Numerology & Life Guidance",
    body: "Insightful readings and life-path guidance that bring clarity to your inner blueprint and the season you are in.",
  },
  {
    step: "Mindfulness & Meditation",
    body: "Grounded practices that build presence, resilience and emotional steadiness in the middle of a busy life.",
  },
  {
    step: "Workshops & Retreats",
    body: "Holistic wellness programmes, workshops and retreats that hold space for deeper transformation and community.",
  },
];

const FOUNDERS = [
  {
    name: "Poulami Ghosh",
    role: "Co-Founder · Therapeutic Vision",
    bio: "For Poulami, music has always been more than a passion — it has been a way of life. Learning music from childhood, she saw first-hand how it could comfort a grieving heart, ease anxiety and give voice to unspoken emotions. This awakening led her to study Music Therapy, Neuroscience, Psychology, Reiki, Mindfulness and Numerology, weaving them into holistic experiences that empower people to reconnect with themselves through sound, self-awareness and compassionate guidance.",
    image: IMAGES.learning,
  },
  {
    name: "Sabyasachi Guin",
    role: "Co-Founder · Creative Direction, Technology & Operations",
    bio: "Sabyasachi is the architect behind Svara Chakra’s ecosystem. With expertise in creative direction, digital experiences, technology and operations, he shapes everything from the brand’s digital presence and user experience to its systems and day-to-day running — ensuring every interaction reflects the heart and purpose of the organisation.",
    image: IMAGES.personal,
  },
];

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <div data-testid="about-page" className="bg-ivory">
      <Seo
        title="About Svara Chakra — Where Sound Meets Science, Wisdom & Human Connection"
        description="The story, philosophy and approach behind Svara Chakra — a refined wellness sanctuary weaving music therapy, energy healing and mindful living into modern life."
        path="/about"
      />
      {/* Hero */}
      <section ref={ref} className="relative h-[70vh] min-h-[480px] overflow-hidden grain">
        <motion.div className="absolute inset-0" style={{ y, scale: 1.1 }}>
          <img
            src={IMAGES.retreats}
            alt="A serene, light-filled retreat setting"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-forest/45" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/70 to-transparent" aria-hidden="true" />
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="kicker text-ivory/80 mb-6"
          >
            About Svara Chakra
          </motion.p>
          <h1 className="font-serif font-light text-ivory text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-4xl">
            <span className="mask-line">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.3 }}
              >
                Where sound &amp; music meet
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span
                className="block italic text-gold"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.44 }}
              >
                science, wisdom &amp; connection.
              </motion.span>
            </span>
          </h1>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-28 md:py-40" data-testid="about-story">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="kicker text-gold mb-6">Our story</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif font-light text-charcoal text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Every meaningful journey begins with a question.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <p className="font-serif font-light text-2xl md:text-3xl text-charcoal leading-[1.4] text-balance">
                What if music could do more than entertain? What if it could
                help people heal, reconnect, and rediscover themselves?
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 text-charcoal/70 leading-relaxed md:text-lg">
                This shared vision brought together Poulami Ghosh and
                Sabyasachi Guin, the founders of Svara Chakra. United by a deep
                belief in the transformative power of sound, human potential
                and conscious living, they created Svara Chakra as a space
                where music, science and holistic wellness come together to
                nurture emotional, mental, physical and spiritual well-being.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-6 text-charcoal/70 leading-relaxed md:text-lg">
                Together, they envisioned Svara Chakra as more than a wellness
                initiative — a sanctuary where healing is experienced, not
                prescribed.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Vision band */}
      <section className="relative bg-forest text-ivory py-28 md:py-40 grain overflow-hidden" data-testid="about-vision">
        <div
          className="absolute inset-0 opacity-[0.1] bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.texture})` }}
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto max-w-[1200px] px-6 md:px-10 text-center">
          <Reveal>
            <p className="kicker text-gold mb-8">Our vision</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.1] tracking-tight text-balance">
              We dream of a world where healing is not seen as fixing what is
              broken, but as remembering who we truly are.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-10 text-ivory/80 leading-relaxed md:text-lg max-w-2xl mx-auto">
              At Svara Chakra, music is not simply performed — it is experienced.
              Sound is not merely heard — it is felt. And every journey begins
              with a single vibration, the one that already exists within you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-28 md:py-40" data-testid="about-approach">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="max-w-2xl mb-16 md:mb-24">
            <Reveal>
              <p className="kicker text-forest mb-6">What we do</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Space, guidance and experiences for your natural healing to unfold.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-charcoal/70 leading-relaxed md:text-lg">
                At Svara Chakra, we believe every individual possesses an innate
                capacity for balance and well-being. Our work brings together:
              </p>
            </Reveal>
          </div>
          <motion.div
            variants={stagger(0.08, 0.16)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20"
          >
            {OFFERINGS.map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="border-t border-stone pt-8">
                <span className="font-serif text-gold text-6xl md:text-7xl leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif font-light text-charcoal text-3xl mt-6 mb-4">
                  {item.step}
                </h3>
                <p className="text-charcoal/70 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Founders */}
      <section className="pb-28 md:pb-40" data-testid="about-founders">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal className="max-w-2xl mb-16 md:mb-20">
            <p className="kicker text-gold mb-6">The founders</p>
            <h2 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.05] tracking-tight">
              The hands that hold the space.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
            {FOUNDERS.map((f, i) => (
              <Reveal key={i} delay={i * 0.08} data-testid={`founder-${i}`}>
                <div className="border-t border-stone pt-10">
                  <span className="font-serif font-light text-gold text-6xl md:text-7xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif font-light text-charcoal text-3xl mt-8">{f.name}</h3>
                  <p className="kicker text-forest mt-3 mb-6 text-[0.62rem]">{f.role}</p>
                  <p className="text-charcoal/70 leading-relaxed max-w-md">{f.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20 md:mt-28 text-center">
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-3 bg-forest text-ivory px-10 py-5 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-charcoal"
            >
              Book a Consultation
              <span>→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
